import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Mail, Lock, User, Eye, EyeOff, Loader2, ArrowRight, ArrowLeft,
  Briefcase, Calculator, FileText, BarChart3, Users, Shield, TrendingUp, CheckCircle2, KeyRound
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const [mode, setMode] = useState("login"); // login | choose-role | register | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { loginWithEmail, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const startResendTimer = () => {
    setResendCooldown(60);
    const id = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(id); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const sendOtp = async () => {
    setOtpSending(true);
    try {
      // Retry twice on network error (transient Cloudflare / proxy / network issues).
      // Uses 20s timeout so slow connections don't false-trigger Network Error.
      let lastErr = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await axios.post(
            `${API}/auth/send-otp`,
            { email: email.trim().toLowerCase() },
            { timeout: 20000 }
          );
          toast.success(`Verification code sent to ${email}`);
          startResendTimer();
          return;
        } catch (err) {
          lastErr = err;
          // Only retry on pure network errors / timeouts (no response from server)
          if (err.response) break;
          await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
        }
      }
      throw lastErr;
    } catch (err) {
      const msg = err.response?.data?.detail
        || (err.message === "Network Error" || err.code === "ECONNABORTED"
            ? "Can't reach the server. Try disabling ad-blockers / VPN, or switch networks. If you already received a code, click 'I have a code' below."
            : err.message)
        || "Failed to send verification code";
      toast.error(msg);
      throw err;
    } finally {
      setOtpSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    if (mode === "register" && !name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      if (mode === "register") {
        // Step 1: send OTP, then transition to OTP verification step
        await sendOtp();
        setMode("otp");
        setLoading(false);
        return;
      }
      const res = await loginWithEmail(email, password);
      toast.success("Welcome back!");
      const userRole = res?.user?.role || "client";
      const redirectPath = userRole === "admin" ? "/admin" : userRole === "ca" ? "/ca/dashboard" : "/dashboard";
      navigate(redirectPath);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtpAndRegister = async (e) => {
    e.preventDefault();
    if (otp.trim().length < 6) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    setLoading(true);
    try {
      // Step 2: verify OTP
      await axios.post(`${API}/auth/verify-otp`, {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
      });
      // Step 3: create account (backend enforces OTP verification)
      await register(email, password, name, role);
      toast.success("Account created!");
      try { window.gtag?.("event", "signup_completed", { role }); } catch { /* ignore */ }
      const redirectPath = role === "ca" ? "/professional/setup" : "/create";
      navigate(redirectPath);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const startRegister = (selectedRole) => {
    setRole(selectedRole);
    setMode("register");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="auth-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src="/assets/logo.png" alt="InvoiceSetu" className="w-10 h-10 rounded-xl object-contain" />
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>InvoiceSetu</span>
          </Link>
        </div>

        {/* LOGIN MODE */}
        {mode === "login" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_20px_60px_rgb(0,0,0,0.06)] p-8">
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Welcome back
            </h1>
            <p className="text-sm text-slate-500 text-center mb-6">
              Sign in to manage your invoices
            </p>

            {/* Google OAuth */}
            <button
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700 mb-6"
              data-testid="google-auth-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-4 text-slate-400">or continue with email</span></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="pl-10"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    className="pl-10 pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="text-right">
                <Link to="/forgot-password" className="text-xs text-blue-600 hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl text-sm font-semibold"
                data-testid="submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Sign In <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            <p className="text-sm text-slate-500 text-center mt-6">
              Don't have an account?{" "}
              <button onClick={() => setMode("choose-role")} className="text-blue-600 font-medium hover:underline" data-testid="switch-to-register">
                Sign up
              </button>
            </p>
          </div>
        )}

        {/* CHOOSE ROLE MODE */}
        {mode === "choose-role" && (
          <div data-testid="role-selection">
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              How will you use InvoiceSetu?
            </h1>
            <p className="text-sm text-slate-500 text-center mb-8">
              Choose your account type to get started
            </p>

            <div className="space-y-4">
              {/* Business Owner Card */}
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => startRegister("client")}
                className="w-full text-left bg-white rounded-2xl border-2 border-slate-200 hover:border-blue-400 shadow-sm hover:shadow-md transition-all p-6 group"
                data-testid="role-card-business"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                    <Briefcase className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-slate-900 mb-1">I'm a Business Owner</h3>
                    <p className="text-sm text-slate-500 mb-3">Create and manage invoices, track payments, file GST returns</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                        <FileText className="w-3 h-3" /> Invoice Builder
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                        <BarChart3 className="w-3 h-3" /> GST Reports
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                        <TrendingUp className="w-3 h-3" /> Analytics
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors mt-1" />
                </div>
              </motion.button>

              {/* CA / Professional Card */}
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => startRegister("ca")}
                className="w-full text-left bg-white rounded-2xl border-2 border-slate-200 hover:border-violet-400 shadow-sm hover:shadow-md transition-all p-6 group"
                data-testid="role-card-ca"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0 group-hover:bg-violet-100 transition-colors">
                    <Calculator className="w-6 h-6 text-violet-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-slate-900 mb-1">I'm a CA / Accountant / Professional</h3>
                    <p className="text-sm text-slate-500 mb-3">Manage multiple clients, review invoices, track GST compliance</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">
                        <Users className="w-3 h-3" /> Multi-Client Dashboard
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">
                        <Shield className="w-3 h-3" /> Professional ID
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded-full">
                        <CheckCircle2 className="w-3 h-3" /> Public Profile
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-violet-500 transition-colors mt-1" />
                </div>
              </motion.button>
            </div>

            <p className="text-sm text-slate-500 text-center mt-6">
              Already have an account?{" "}
              <button onClick={() => setMode("login")} className="text-blue-600 font-medium hover:underline" data-testid="switch-to-login">
                Sign in
              </button>
            </p>
          </div>
        )}

        {/* REGISTER MODE (after role selection) */}
        {mode === "register" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_20px_60px_rgb(0,0,0,0.06)] p-8">
            {/* Role indicator */}
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => setMode("choose-role")}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="back-to-role-select"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
                role === "ca"
                  ? "bg-violet-50 text-violet-700 border border-violet-200"
                  : "bg-blue-50 text-blue-700 border border-blue-200"
              }`}>
                {role === "ca" ? <Calculator className="w-4 h-4" /> : <Briefcase className="w-4 h-4" />}
                {role === "ca" ? "CA / Professional" : "Business Owner"}
              </div>
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Create your account
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              {role === "ca"
                ? "Set up your professional account to manage clients"
                : "Start managing invoices in 60 seconds"
              }
            </p>

            {/* Google OAuth */}
            <button
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700 mb-6"
              data-testid="google-auth-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-white px-4 text-slate-400">or continue with email</span></div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={role === "ca" ? "CA Rajesh Kumar" : "John Doe"}
                    className="pl-10"
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={role === "ca" ? "ca@firm.com" : "you@company.com"}
                    className="pl-10"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    className="pl-10 pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={`w-full py-3 rounded-xl text-sm font-semibold ${
                  role === "ca"
                    ? "bg-violet-600 hover:bg-violet-700 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
                data-testid="submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Continue <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
              <p className="text-[11px] text-slate-400 text-center">
                We'll send a 6-digit code to verify your email.
              </p>
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => {
                    if (!email.trim() || !name.trim() || password.length < 6) {
                      toast.error("Fill in name, email and password first");
                      return;
                    }
                    setMode("otp");
                  }}
                  className="text-xs text-slate-500 hover:text-blue-600"
                  data-testid="already-have-code"
                >
                  Already received a code? Enter it here
                </button>
              </div>
            </form>

            <p className="text-sm text-slate-500 text-center mt-6">
              Already have an account?{" "}
              <button onClick={() => setMode("login")} className="text-blue-600 font-medium hover:underline" data-testid="switch-to-login">
                Sign in
              </button>
            </p>
          </div>
        )}

        {/* OTP VERIFICATION MODE */}
        {mode === "otp" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_20px_60px_rgb(0,0,0,0.06)] p-8" data-testid="otp-step">
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => setMode("register")}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                data-testid="back-to-register"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
                role === "ca"
                  ? "bg-violet-50 text-violet-700 border border-violet-200"
                  : "bg-blue-50 text-blue-700 border border-blue-200"
              }`}>
                <KeyRound className="w-4 h-4" />
                Verify your email
              </div>
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Enter verification code
            </h1>
            <p className="text-sm text-slate-500 mb-6">
              We sent a 6-digit code to <span className="font-medium text-slate-700">{email}</span>. It expires in 10 minutes.
            </p>

            <form onSubmit={handleVerifyOtpAndRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Verification code</label>
                <Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="text-center text-2xl tracking-[0.5em] font-mono font-semibold"
                  data-testid="otp-input"
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                disabled={loading || otp.length < 6}
                className={`w-full py-3 rounded-xl text-sm font-semibold ${
                  role === "ca"
                    ? "bg-violet-600 hover:bg-violet-700 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
                data-testid="verify-otp-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Verify & Create Account <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  disabled={resendCooldown > 0 || otpSending}
                  onClick={async () => {
                    try { await sendOtp(); setOtp(""); } catch {}
                  }}
                  className="text-xs text-slate-500 hover:text-blue-600 disabled:text-slate-400 disabled:cursor-not-allowed"
                  data-testid="resend-otp-btn"
                >
                  {otpSending ? "Sending…" : resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Didn't get it? Resend code"}
                </button>
              </div>
            </form>
          </div>
        )}

        <p className="text-xs text-slate-400 text-center mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </motion.div>
    </div>
  );
}
